import React, { useEffect, useMemo, useState } from "react";
import VesselTrackingMap from "./VesselTrackingMap";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";

// Where "Request Booking" goes (sailingId is appended). The public booking request page.
const BOOKING_URL = "https://shipstock.app/request-booking";

// Same set the customer portal shows: open, loading, departed, arrived.
const PUBLIC_STATUSES = ["open", "loading", "departed", "arrived"];

// Show the live "Track Vessel" map (needs the vessel's mmsi). Set false to hide it publicly.
const SHOW_TRACKING = true;

const barClasses = {
  open: "bg-emerald-600 text-white",
  confirmed: "bg-sky-600 text-white",
  loading: "bg-blue-600 text-white",
  departed: "bg-purple-600 text-white",
  arrived: "bg-slate-700 text-white",
  closed: "bg-gray-500 text-white",
};

const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const startOfDay = (v) => { const d = new Date(v); d.setHours(0, 0, 0, 0); return d; };
const dayDiff = (a, b) => Math.round((startOfDay(b) - startOfDay(a)) / 86400000);
const validDate = (v) => { if (!v) return null; const d = new Date(v); return Number.isNaN(d.getTime()) ? null : d; };

const NZ = "Pacific/Auckland";
const fmtDateTime = (v) => {
  const d = validDate(v);
  return d ? d.toLocaleString("en-NZ", { timeZone: NZ, weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "TBC";
};
const fmtTime = (v) => {
  const d = validDate(v);
  return d ? d.toLocaleTimeString("en-NZ", { timeZone: NZ, hour: "numeric", minute: "2-digit" }) : "";
};

// Only these fields ever reach the screen — no customers, bookings or capacity.
const toPublic = (s) => ({
  id: s._id || s.id,
  ref: s.sailingRef || s.ref || String(s._id || s.id || "").slice(-6).toUpperCase(),
  origin: s.origin || "",
  destination: s.destination || "",
  route: s.routeLabel || s.route || `${s.origin || "-"} → ${s.destination || "-"}`,
  vessel: s.vesselName || s.vessel?.name || (typeof s.vessel === "string" ? s.vessel : "") || "",
  vesselId: s.vessel?._id || (typeof s.vessel === "string" ? s.vessel : ""),
  mmsi: s.vesselMmsi || s.vessel?.mmsi || "",
  etd: s.etd || null,
  eta: s.eta || null,
  bookingCutoff: s.bookingCutoff || null,
  status: String(s.status || "").toLowerCase(),
});

export default function PublicSchedule() {
  const embed = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("embed") === "1";
  const [sailings, setSailings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState("list");
  const [month, setMonth] = useState(new Date());
  const [vessel, setVessel] = useState("all");
  const [vesselList, setVesselList] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError("");
        // Preferred: a dedicated public endpoint. Falls back to the sailings list.
        let res = await fetch(`${API_BASE}/public/schedule`);
        if (!res.ok) res = await fetch(`${API_BASE}/sailings`);
        if (!res.ok) throw new Error("Could not load the schedule.");
        const data = await res.json();
        const list = Array.isArray(data) ? data : data?.data || data?.sailings || [];
        setSailings(list.map(toPublic));
        // Fallback only: the public endpoint already includes the vessel mmsi.
        if (SHOW_TRACKING && !list.some((x) => x.vesselMmsi)) {
          try {
            const vr = await fetch(`${API_BASE}/vessels`);
            const vd = await vr.json();
            setVesselList(Array.isArray(vd) ? vd : vd?.data || []);
          } catch (e) { /* tracking just won't show */ }
        }
      } catch (err) {
        setError(err.message || "Could not load the schedule.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const visible = useMemo(
    () => sailings.filter((s) => s.etd && PUBLIC_STATUSES.includes(s.status)).sort((a, b) => new Date(a.etd) - new Date(b.etd)),
    [sailings]
  );
  const vessels = useMemo(() => [...new Set(visible.map((s) => s.vessel).filter(Boolean))].sort(), [visible]);
  const filtered = useMemo(
    () => visible.filter((s) => (vessel === "all" || s.vessel === vessel)),
    [visible, vessel]
  );

  const today = startOfDay(new Date());
  const upcoming = filtered.filter((s) => {
    const end = validDate(s.eta) || validDate(s.etd);
    return startOfDay(end) >= today || s.status === "loading" || s.status === "departed";
  });

  const weeks = useMemo(() => {
    const y = month.getFullYear(), m = month.getMonth();
    let offset = new Date(y, m, 1).getDay();
    offset = offset === 0 ? 6 : offset - 1;
    const count = Math.ceil((offset + new Date(y, m + 1, 0).getDate()) / 7);
    return Array.from({ length: count }, (_, w) => {
      const days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date(y, m, 1 - offset + w * 7 + i);
        return { date, day: date.getDate(), inMonth: date.getMonth() === m, isToday: dayDiff(today, date) === 0 };
      });
      const ws = days[0].date, we = days[6].date;
      const bars = [];
      filtered.forEach((s) => {
        const start = startOfDay(s.etd);
        let end = validDate(s.eta) ? startOfDay(s.eta) : start;
        if (end < start) end = start;
        if (end < ws || start > we) return;
        bars.push({ s, start, total: dayDiff(start, end) + 1, startCol: Math.max(dayDiff(ws, start), 0), endCol: Math.min(dayDiff(ws, end), 6), contLeft: start < ws, contRight: end > we });
      });
      bars.sort((a, b) => a.startCol - b.startCol || b.endCol - a.endCol);
      const laneEnds = [];
      bars.forEach((bar) => {
        let lane = laneEnds.findIndex((e) => e < bar.startCol);
        if (lane === -1) { lane = laneEnds.length; laneEnds.push(bar.endCol); } else laneEnds[lane] = bar.endCol;
        bar.lane = lane;
      });
      const perCol = Array(7).fill(0);
      const cutoffs = [];
      filtered.forEach((s) => {
        const when = validDate(s.bookingCutoff);
        if (!when || s.status === "closed") return;
        const d = startOfDay(when);
        if (d < ws || d > we) return;
        const col = dayDiff(ws, d);
        cutoffs.push({ s, when, col, row: perCol[col] });
        perCol[col] += 1;
      });
      return { days, bars, cutoffs, cutoffRows: Math.max(...perCol) };
    });
    // eslint-disable-next-line
  }, [month, filtered]);

  return (
    <div className={embed ? "w-full" : "min-h-screen bg-gray-50"}>
      <div className={embed ? "space-y-4 p-3" : "mx-auto w-full max-w-6xl space-y-6 p-6"}>
        {!embed && (
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Sailing Schedule</h1>
              <p className="mt-1 text-sm text-gray-500">Upcoming sailings open for bookings. Updated live — times are New Zealand time.</p>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm md:flex-row md:items-center md:justify-between">
          <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
            {["list", "calendar"].map((v) => (
              <button key={v} onClick={() => setView(v)}
                className={`rounded-md px-4 py-1.5 text-sm font-medium ${view === v ? "bg-black text-white" : "text-gray-700 hover:bg-gray-100"}`}>
                {v === "list" ? "Upcoming" : "Calendar"}
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <select value={vessel} onChange={(e) => setVessel(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm">
              <option value="all">All vessels</option>
              {vessels.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        </div>

        {loading && <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">Loading schedule…</div>}
        {error && !loading && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}

        {!loading && !error && view === "list" && (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            {upcoming.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-gray-400">No upcoming sailings open for bookings right now. Contact McCallum Bros for more information.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-200 bg-gray-50">
                    <tr>
                      {["Sailing Ref", "Route", "Vessel", "ETD", "ETA", "Booking Cutoff", "Status", ""].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {upcoming.map((s, i) => {
                      const cutoff = validDate(s.bookingCutoff);
                      const cutoffPassed = cutoff && cutoff < new Date();
                      const mmsi = s.mmsi || vesselList.find((v) => v._id === s.vesselId || v.name === s.vessel)?.mmsi;
                      const canTrack = SHOW_TRACKING && ["loading", "departed"].includes(s.status) && mmsi;
                      const isOpen = expandedId === s.id;
                      return (
                        <React.Fragment key={s.id}>
                          <tr className={i > 0 ? "border-t border-gray-200" : ""}>
                            <td className="px-4 py-3 font-medium text-gray-900">{s.ref}</td>
                            <td className="px-4 py-3 text-gray-600">{s.origin} → {s.destination}</td>
                            <td className="px-4 py-3 text-gray-600">{s.vessel || "—"}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-gray-600">{s.etd ? new Date(s.etd).toLocaleDateString("en-NZ", { timeZone: NZ, day: "2-digit", month: "short", year: "numeric" }) : "TBC"}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-gray-600">{s.eta ? new Date(s.eta).toLocaleDateString("en-NZ", { timeZone: NZ, day: "2-digit", month: "short", year: "numeric" }) : "TBC"}</td>
                            <td className={`whitespace-nowrap px-4 py-3 ${cutoffPassed ? "text-gray-400 line-through" : "text-gray-600"}`}>{cutoff ? cutoff.toLocaleDateString("en-NZ", { timeZone: NZ, day: "2-digit", month: "short" }) : "—"}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium capitalize ${
                                s.status === "open" ? "bg-emerald-50 text-emerald-700" :
                                s.status === "loading" ? "bg-blue-50 text-blue-700" :
                                s.status === "departed" ? "bg-purple-50 text-purple-700" :
                                s.status === "arrived" ? "bg-slate-100 text-slate-600" : "bg-amber-50 text-amber-700"
                              }`}>{s.status}</span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                {canTrack && (
                                  <button onClick={() => setExpandedId(isOpen ? null : s.id)}
                                    className="inline-flex w-32 items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50">
                                    {isOpen ? "Hide Map" : "Track Vessel"}
                                  </button>
                                )}
                                {s.status === "open" && !cutoffPassed && (
                                  <a href={`${BOOKING_URL}?sailingId=${s.id}`} target="_top" rel="noopener"
                                    className="inline-flex w-32 items-center justify-center gap-1.5 rounded-lg bg-slate-950 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800">
                                    Request Booking
                                  </a>
                                )}
                              </div>
                            </td>
                          </tr>
                          {isOpen && canTrack && (
                            <tr className="border-t border-gray-100 bg-gray-50">
                              <td colSpan={8} className="px-4 py-4">
                                <VesselTrackingMap mmsi={mmsi} vesselName={s.vessel} height={320} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {!loading && !error && view === "calendar" && (
          <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
            <div className="flex items-center justify-between bg-slate-900 px-4 py-3 text-white">
              <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="rounded-lg border border-white/20 px-3 py-1.5 text-sm hover:bg-white/10">‹ Prev</button>
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold">{monthNames[month.getMonth()]} {month.getFullYear()}</h2>
                <button onClick={() => setMonth(new Date())} className="rounded-lg border border-white/20 px-2.5 py-1 text-xs hover:bg-white/10">Today</button>
              </div>
              <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="rounded-lg border border-white/20 px-3 py-1.5 text-sm hover:bg-white/10">Next ›</button>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-gray-200 bg-gray-50 px-4 py-2 text-[11px] text-gray-600">
              {PUBLIC_STATUSES.map((k) => (
                <span key={k} className="inline-flex items-center gap-1.5"><span className={`inline-block h-2.5 w-2.5 rounded-sm ${barClasses[k].split(" ")[0]}`} />{k[0].toUpperCase() + k.slice(1)}</span>
              ))}
              <span className="inline-flex items-center gap-1.5"><span className="rounded border border-rose-200 bg-rose-50 px-1 text-[9px] font-bold text-rose-700">CUT-OFF</span>Booking cut-off</span>
            </div>
            <div className="grid grid-cols-7 border-b border-gray-300 bg-gray-50">
              {dayNames.map((d) => <div key={d} className="border-r border-gray-200 px-2 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600 last:border-r-0">{d}</div>)}
            </div>
            <div className="overflow-x-auto">
              <div className="min-w-[720px]">
                {weeks.map((week, wi) => (
                  <div key={wi} className="relative border-b border-gray-200 last:border-b-0">
                    <div className="pointer-events-none absolute inset-0 grid grid-cols-7">
                      {week.days.map((d, di) => <div key={di} className={`border-r border-gray-200 last:border-r-0 ${d.isToday ? "bg-amber-50" : d.inMonth ? "bg-white" : "bg-gray-50"}`} />)}
                    </div>
                    <div className="relative grid grid-cols-7">
                      {week.days.map((d, di) => (
                        <div key={di} className="px-2 pt-1.5">
                          <span className={`inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-1 text-xs font-semibold ${d.isToday ? "bg-black text-white" : d.inMonth ? "text-gray-800" : "text-gray-400"}`}>{d.day}</span>
                        </div>
                      ))}
                    </div>
                    <div className="relative px-1 pb-2 pt-1" style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", rowGap: 4, minHeight: 60 }}>
                      {week.cutoffs.map((c) => (
                        <div key={`co-${c.s.id}`} title={`Booking cut-off for ${c.s.route}: ${fmtDateTime(c.s.bookingCutoff)}`}
                          style={{ gridColumn: `${c.col + 1} / ${c.col + 2}`, gridRow: c.row + 1 }}
                          className="mx-0.5 flex items-center gap-1 truncate rounded border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700">
                          <span className="font-bold">CUT-OFF</span><span className="truncate">{c.s.route} · {fmtTime(c.when)}</span>
                        </div>
                      ))}
                      {week.bars.map((bar) => {
                        const cols = bar.endCol - bar.startCol + 1;
                        return (
                          <div key={`bar-${bar.s.id}`}
                            title={`${bar.s.route}\n${bar.s.vessel}\nETD ${fmtDateTime(bar.s.etd)}\nETA ${fmtDateTime(bar.s.eta)}`}
                            style={{ gridColumn: `${bar.startCol + 1} / ${bar.endCol + 2}`, gridRow: week.cutoffRows + bar.lane + 1 }}
                            className={`mx-0.5 flex min-w-0 flex-col overflow-hidden shadow-sm ${barClasses[bar.s.status] || "bg-gray-500 text-white"} ${bar.contLeft ? "rounded-l-none" : "rounded-l-xl"} ${bar.contRight ? "rounded-r-none" : "rounded-r-xl"}`}>
                            <div className="flex items-center gap-1.5 px-2 pt-1.5 text-[11px] leading-tight">
                              {bar.contLeft && <span className="opacity-70">◀</span>}
                              <span className="truncate font-bold">{bar.s.route}</span>
                              {bar.s.vessel && <span className="ml-auto hidden shrink-0 rounded bg-black/20 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide sm:inline">{bar.s.vessel}</span>}
                              {bar.contRight && <span className="opacity-70">▶</span>}
                            </div>
                            <div className="mt-1 flex pb-0.5">
                              {Array.from({ length: cols }, (_, ci) => {
                                const idx = dayDiff(bar.start, week.days[bar.startCol + ci].date) + 1;
                                const first = idx === 1, last = idx === bar.total;
                                let label;
                                if (bar.total === 1) label = `${fmtTime(bar.s.etd)} → ${fmtTime(bar.s.eta || bar.s.etd)}`;
                                else if (first) label = `Depart ${fmtTime(bar.s.etd)}`;
                                else if (last) label = `Arrive ${fmtTime(bar.s.eta)}`;
                                else label = `At sea · Day ${idx}/${bar.total}`;
                                return <div key={ci} className={`min-w-0 flex-1 truncate border-t border-white/20 px-1 py-1 text-center text-[9px] font-semibold uppercase tracking-wide ${ci > 0 ? "border-l border-l-white/20" : ""} ${first || last ? "bg-black/25" : "bg-black/10"}`}>{label}</div>;
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {!embed && <p className="text-center text-xs text-gray-400">Bookings must be submitted before the cutoff date shown. Contact McCallum Bros directly for urgent requests.</p>}
      </div>
    </div>
  );
}